import { useState, useCallback } from 'react'

const STORAGE_KEY = 'hapana-search-recents'
const MAX_ITEMS = 5

export interface RecentItem {
  id: string
  name: string
  section: string
  href: string
  timestamp: number
}

function loadRecents(): RecentItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []
    return JSON.parse(stored) as RecentItem[]
  } catch {
    return []
  }
}

function saveRecents(items: RecentItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // localStorage full or unavailable
  }
}

export function useRecentItems() {
  const [recentItems, setRecentItems] = useState<RecentItem[]>(loadRecents)

  const addRecentItem = useCallback((item: Omit<RecentItem, 'timestamp'>) => {
    setRecentItems((prev) => {
      const filtered = prev.filter((r) => r.id !== item.id)
      const updated = [{ ...item, timestamp: Date.now() }, ...filtered].slice(0, MAX_ITEMS)
      saveRecents(updated)
      return updated
    })
  }, [])

  return { recentItems, addRecentItem }
}

export function formatRelativeTime(
  timestamp: number,
  t?: (key: string, options?: Record<string, unknown>) => string
): string {
  const diff = Date.now() - timestamp
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (t) {
    if (seconds < 60) return t('relativeTime.justNow')
    if (minutes < 60) return t('relativeTime.minutesAgo', { count: minutes })
    if (hours < 24) return t('relativeTime.hoursAgo', { count: hours })
    if (days === 1) return t('relativeTime.yesterday')
    return t('relativeTime.daysAgo', { count: days })
  }

  // Fallback when no translation function is provided
  if (seconds < 60) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (hours < 24) return `${hours} hrs ago`
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}
